import { Button, Card, CardBody, Col, Container, Row } from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import LoadingSpiner from '../components/LoadingSpiner';
import {
  capitalizeWords,
  formatPhoneNumber,
  formatPrice,
} from '../components/capitalizeFunction';
import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { useAllCommandes, useDeleteCommande } from '../../Api/queriesCommande';
import { useNavigate } from 'react-router-dom';

export default function CommandeListe() {
  /**
   * OPTIMISATION PRO (Historique des Commandes)
   *
   * Avant:
   * - `useAllCommandes()` chargeait toute la liste + toutes les factures
   * - Puis recherche/filtre côté navigateur => lourd RAM/CPU et lent
   *
   * Maintenant:
   * - On active le mode backend `paged=1` (même endpoint, juste query params)
   * - Recherche côté serveur (`q`)
   * - Pagination => on ne charge qu'une page
   * - On garde les mêmes variables/structures attendues (`commandesListe`, `factures`)
   */
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const { mutate: deleteCommandeAndRestorStock } = useDeleteCommande();

  // State de chargement pour la suppression
  const [isDeleting, setIsDeletting] = useState(false);

  // Annuler une Ordonnance
  const navigate = useNavigate();

  // Navigation ver la FACTURE avec ID de Paiement
  const handleCommandeClick = (id) => {
    navigate(`/facture/${id}`);
  };

  // ---------------------------
  // Fonction pour exeuter l'annulation de la décrementation des stocks
  function deleteCommande(comm) {
    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-success ms-2',
        cancelButton: 'btn btn-danger me-2',
      },
      buttonsStyling: false,
    });

    swalWithBootstrapButtons
      .fire({
        title: `Attention après l'Annulation les produits seront ajouter sur votre STOCK !  `,
        text: 'Voulez-vous continuer ?',
        icon: 'question',
        iconColor: 'red',
        showCancelButton: true,
        confirmButtonText: 'Oui, Continuer',
        cancelButtonText: 'Non',
        reverseButtons: true,
      })
      .then((result) => {
        if (result.isConfirmed) {
          try {
            const payload = {
              commandeId: comm?._id,
              items: comm?.items.map((item) => ({
                produit: item.produit,
                quantity: item.quantity,
              })),
            };

            // --------------------------------
            setIsDeletting(true);
            // Exécuter l'annulation
            deleteCommandeAndRestorStock(payload, {
              onSuccess: () => {
                setIsDeletting(false);
                swalWithBootstrapButtons.fire({
                  title: 'Succès!',
                  text: `Commande Annulé avec succès les produits sont ajouté sur le STOCK.`,
                  icon: 'success',
                });
              },
              onError: (e) => {
                setIsDeletting(false);
                swalWithBootstrapButtons.fire({
                  title: 'Erreur',
                  text:
                    e?.response?.data?.message ||
                    'Une erreur est survenue lors de la suppression.',
                  icon: 'error',
                });
              },
            });
          } catch (e) {
            setIsDeletting(false);
            swalWithBootstrapButtons.fire({
              title: 'Erreur',
              text:
                e ||
                e?.response?.data?.message ||
                "Une erreur est survenue lors de l'Annulation.",
              icon: 'error',
            });
          }
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          setIsDeletting(false);
          swalWithBootstrapButtons.fire({
            title: 'Commande non Annulée',
            icon: 'error',
          });
        }
      });
  }
  // ------------------------------------------------------------

  const [searchTerm, setSearchTerm] = useState('');
  const [todayCommande, setTodayCommande] = useState(false);
  const [delivredCommande, setDelivredCommande] = useState(false);
  const [notDelivredCommande, setNotdelivredCommande] = useState(false);
  // Debounce pour éviter un appel réseau à chaque frappe
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  React.useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Mapping filtres UI => filtres serveur
  // - On conserve vos states (todayCommande, delivredCommande, notDelivredCommande)
  // - On les utilise pour construire des query params sans changer l'API
  const statutParam = delivredCommande
    ? 'en cours'
    : notDelivredCommande
      ? 'en attente'
      : '';

  const { data: commandes, isLoading, error } = useAllCommandes({
    paged: 1,
    page,
    limit,
    q: debouncedSearch,
    today: todayCommande ? 1 : 0,
    statut: statutParam,
  });

  // Données paginées
  const filterCommandes = commandes?.commandesListe || [];
  const total = commandes?.total ?? 0;
  const totalPages = commandes?.totalPages ?? 1;

  // Total Commandes Livrés
  const totalCommandesLivres = filterCommandes?.filter(
    (comm) => comm?.statut.toLowerCase() === 'livré'
  )?.length;

  // Commande en Attente
  const commandesEnAttente = filterCommandes?.filter((comm) => {
    return comm?.statut.toLowerCase() === 'en attente';
  });

  // Commande en Cours
  const commandesEnCours = filterCommandes?.filter(
    (comm) => comm?.statut.toLowerCase() === 'en cours'
  );

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <Breadcrumbs
            title='Commande'
            breadcrumbItem='Historique des Commandes'
          />
          {/* -------------------------- */}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div id='commandeList'>
                    <div
                      className='search-box me-2 d-flex align-items-center gap-2'
                      style={{ width: '200px' }}
                    >
                      {searchTerm !== '' && (
                        <Button
                          color='danger'
                          onClick={() => setSearchTerm('')}
                        >
                          <i className='fas fa-window-close'></i>
                        </Button>
                      )}
                      <input
                        type='text'
                        className='form-control search border border-dark rounded'
                        placeholder='Rechercher...'
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>

                    {/* Pagination (en haut) */}
                    {!error && !isLoading && totalPages > 1 && (
                      <div className='d-flex align-items-center gap-2 flex-wrap mt-3'>
                        <div
                          className='d-inline-flex gap-2'
                          role='group'
                          aria-label='Pagination commandes'
                        >
                          <Button
                            color='info'
                            className='shadow-sm'
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            <i className='bx bx-chevron-left'></i>
                          </Button>
                          <Button
                            color='primary'
                            className='shadow-sm'
                            disabled={page >= totalPages}
                            onClick={() =>
                              setPage((p) => Math.min(totalPages, p + 1))
                            }
                          >
                            <i className='bx bx-chevron-right'></i>
                          </Button>
                        </div>
                        <span className='small fw-semibold text-dark'>
                          Page <span className='badge bg-primary'>{page}</span> /{' '}
                          <span className='badge bg-primary'>{totalPages}</span> ·{' '}
                          <span className='badge bg-info'>{total}</span> résultats
                        </span>
                        <div className='d-flex align-items-center gap-2'>
                          <span className='text-dark small fw-semibold'>
                            Par page
                          </span>
                          <select
                            className='form-select form-select-sm border border-primary'
                            style={{ width: 95 }}
                            value={limit}
                            onChange={(e) => {
                              setLimit(Number(e.target.value));
                              setPage(1);
                            }}
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                  {!isLoading && !error && (
                    <div className='d-flex  justify-content-around align-items-center flex-wrap'>
                      <div className='d-flex flex-column justify-content-center align-items-center gap-2 '>
                        <div className='d-flex flex-wrap justify-content-center align-items-center gap-2 text-warning'>
                          <label
                            className='form-check-label'
                            htmlFor='filterToday'
                          >
                            Aujourd'hui
                          </label>{' '}
                          <input
                            type='checkbox'
                            className='form-check-input'
                            id='filterToday'
                            onChange={() => setTodayCommande(!todayCommande)}
                          />
                        </div>
                        <div className='d-flex flex-wrap justify-content-center align-items-center gap-2 text-warning'>
                          <label
                            className='form-check-label'
                            htmlFor='filterDelivredCommande'
                          >
                            En Cours
                          </label>{' '}
                          <input
                            type='checkbox'
                            className='form-check-input'
                            id='filterDelivredCommande'
                            onChange={() =>
                              setDelivredCommande(!delivredCommande)
                            }
                          />
                        </div>
                        <div className='d-flex flex-wrap justify-content-center align-items-center gap-2 text-warning'>
                          <label
                            className='form-check-label'
                            htmlFor='filterNotDelivredCommande'
                          >
                            En Attente
                          </label>
                          <input
                            type='checkbox'
                            className='form-check-input'
                            id='filterNotDelivredCommande'
                            onChange={() =>
                              setNotdelivredCommande(!notDelivredCommande)
                            }
                          />
                        </div>
                      </div>

                      <Row className='mt-4 d-flex flex-column justify-content-center align-items-center'>
                        <Col>
                          <h6 className='text-center font-size-15 mt-2'>
                            Total:{' '}
                            <span className='text-info font-size-18'>
                              {' '}
                              {formatPrice(totalCommandesLivres)}
                            </span>
                          </h6>
                        </Col>

                        <Col>
                          <h6 className='text-center font-size-15 mt-2'>
                            En Cours:{' '}
                            <span className='text-info font-size-18'>
                              {' '}
                              {formatPrice(commandesEnCours?.length)}
                            </span>
                          </h6>
                        </Col>
                        <Col>
                          <h6 className='text-center font-size-15 mt-2'>
                            En Attente:{' '}
                            <span className='text-danger font-size-18'>
                              {' '}
                              {formatPrice(commandesEnAttente?.length)}
                            </span>
                          </h6>
                        </Col>
                      </Row>
                    </div>
                  )}
                  {error && (
                    <div className='text-danger text-center'>
                      Erreur de chargement des données
                    </div>
                  )}
                  {isLoading && <LoadingSpiner />}

                  <div className='table-responsive table-card mt-3 mb-1'>
                    {filterCommandes?.length === 0 && (
                      <div className='text-center text-mutate'>
                        Aucune commande pour le moment !
                      </div>
                    )}
                    {!error && !isLoading && filterCommandes?.length > 0 && (
                      <table
                        className='table align-middle table-nowrap table-hover'
                        id='commandeTable'
                      >
                        <thead className='table-light'>
                          <tr>
                            <th scope='col' style={{ width: '50px' }}>
                              <i className='fas fa-dollar-sign text-warning'></i>
                            </th>
                            <th scope='col' style={{ width: '50px' }}>
                              Date de Commande
                            </th>
                            <th className='sort' data-sort='fullName'>
                              Client
                            </th>
                            <th className='sort' data-sort='phoneNumber'>
                              Téléphone
                            </th>
                            <th className='sort' data-sort='adresse'>
                              Adresse de Livraison
                            </th>
                            <th className='sort' data-sort='items'>
                              Article
                            </th>
                            <th className='sort' data-sort='statut'>
                              Statut
                            </th>

                            <th className='sort' data-sort='action'>
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody className='list form-check-all text-center'>
                          {filterCommandes?.length > 0 &&
                            filterCommandes?.map((comm) => (
                              <tr key={comm?._id}>
                                <th scope='row'>
                                  {commandes?.factures?.some(
                                    (fact) => fact?.commande?._id === comm?._id
                                  ) ? (
                                    <i className='fas fa-check-circle text-success'></i>
                                  ) : (
                                    <i className='fas fa-times-circle text-danger'></i>
                                  )}{' '}
                                </th>
                                <th scope='row'>
                                  {new Date(
                                    comm?.commandeDate
                                  ).toLocaleDateString('fr-Fr', {
                                    weekday: 'short',
                                    year: 'numeric',
                                    month: '2-digit',
                                    day: '2-digit',
                                  })}
                                </th>
                                <td>{capitalizeWords(comm?.fullName)}</td>
                                <td>
                                  {formatPhoneNumber(comm?.phoneNumber) ||
                                    '------'}
                                </td>
                                <td>{capitalizeWords(comm?.adresse)}</td>

                                <td>
                                  {comm?.items?.length} acticles
                                  {'  '}
                                </td>
                                <td>
                                  <span
                                    className={`badge badge-soft-${
                                      comm?.statut === 'livré'
                                        ? 'success'
                                        : 'warning'
                                    }
                                         text-uppercase`}
                                  >
                                    {comm?.statut}
                                  </span>
                                </td>

                                <td>
                                  {isDeleting && <LoadingSpiner />}
                                  {!isDeleting && (
                                    <div className='d-flex gap-2'>
                                      <div className='show-details'>
                                        <button
                                          className='btn btn-sm btn-info show-item-btn'
                                          data-bs-toggle='modal'
                                          data-bs-target='#showdetails'
                                          onClick={() => {
                                            handleCommandeClick(comm?._id);
                                          }}
                                        >
                                          <i className=' bx bx-show-alt text-white'></i>
                                        </button>
                                      </div>
                                      <div className='d-flex gap-2'>
                                        <div className='edit'>
                                          <button
                                            className='btn btn-sm btn-success edit-item-btn'
                                            onClick={() => {
                                              navigate(
                                                `/updateCommande/${comm?._id}`
                                              );
                                            }}
                                          >
                                            <i className='ri-pencil-fill text-white'></i>
                                          </button>
                                        </div>
                                        <div className='remove'>
                                          <button
                                            className='btn btn-sm btn-danger remove-item-btn'
                                            data-bs-toggle='modal'
                                            data-bs-target='#deleteRecordModal'
                                            onClick={() => {
                                              deleteCommande(comm);
                                            }}
                                          >
                                            <i className='ri-delete-bin-fill text-white'></i>
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}
